import {
  API_V1,
  dailyRewardClaimSchema,
  dailyRewardSchema,
  errorEnvelopeSchema,
  healthResponseSchema,
  leaveRoomResponseSchema,
  lobbyResponseSchema,
  shopCatalogSchema,
  shopInventorySchema,
  shopPurchaseSuccessSchema,
  type DailyReward,
  type DailyRewardClaim,
  type HealthResponse,
  type JoinRoomResponse,
  type LeaveRoomResponse,
  type LobbyResponse,
  type SeatReservation,
  type ShopCatalog,
  type ShopInventory,
  type ShopPurchaseRequest,
  type ShopPurchaseSuccess,
} from "@wr/contracts";
import { t } from "@wr/i18n";
import { API_URL, hasApiUrl } from "../env";
import type { RejoinRefusalFacts } from "./room";

/**
 * The one place the SPA talks HTTP to `apps/api`.
 *
 * Every failure becomes an `ApiRequestError` carrying the server's error
 * envelope (`{ error: { code, message } }` — the shape S1-4 fixed in
 * `@wr/contracts`) when there is one, so screens render one branch instead of
 * five.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /**
   * The envelope's `details`, untyped on purpose.
   *
   * Two refusals carry structured data the client acts on and neither is a
   * status code: `insufficient_funds` names the balance and the fee (Ivy's
   * "need {n} more" is derived from the same number the coin chip shows), and
   * `seat_closed` names the room the spent-room set has to remember (lobby
   * §12.4). Readers narrow it themselves — `reasonOf` / `detailString` below —
   * rather than this class growing a union of every route's shape.
   */
  readonly details: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** `details.reason`, when there is one. The discriminator every refusal carries. */
export function reasonOf(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === "string" ? reason : null;
}

/**
 * **The §5.5 register split, asked of an error: is this STATE, or transport?**
 *
 * A typed refusal is the server answering the question — a 4xx carrying the
 * envelope's `details.reason`. It is a fact, it will be the same fact a
 * millisecond later, and *there is nothing to retry*. Everything else (a
 * rejected `fetch` with no status, a 5xx, a body that is not an envelope) is
 * the pipe failing, which is exactly what retrying is for.
 *
 * Lives here, beside `reasonOf`, because the distinction is a property of the
 * error and not of any one screen: T-407 NB1 found the split honoured in the
 * dialog's own copy registers while the *fetch layer* underneath it treated a
 * `409 shop_unavailable` as a flaky network — four attempts and ~7s of
 * skeletons before the closed panel a single answer had already settled.
 *
 * Deliberately not wired into the app-level `QueryClient` default: this
 * predicate is correct for reads, and making it the default for every mutation
 * on the way past would be a money-path decision taken sideways.
 */
export function isStateRefusal(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  if (error.status < 400 || error.status >= 500) return false;
  return reasonOf(error) !== null;
}

/** One string field off `details`, or `null`. Never a cast at the call site. */
export function detailString(error: unknown, field: string): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const value = (details as Record<string, unknown>)[field];
  return typeof value === "string" ? value : null;
}

/** One numeric field off `details`, or `null`. */
export function detailNumber(error: unknown, field: string): number | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const value = (details as Record<string, unknown>)[field];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function requireApiUrl(): string {
  if (!hasApiUrl()) throw new ApiRequestError(t("error.missingApiUrl"), 0, "unavailable");
  return API_URL;
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const base = requireApiUrl();

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      // `web` and `api` are different origins in every deployed environment,
      // so the session cookie only travels when the request opts in. Without
      // this every authenticated route looks logged-out from the browser.
      credentials: "include",
      ...init,
      headers: {
        accept: "application/json",
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch {
    // fetch rejects on DNS/TLS/CORS-preflight failures — there is no status.
    throw new ApiRequestError(t("error.network"), 0, "unavailable");
  }

  const text = await response.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }

  if (!response.ok) {
    const envelope = errorEnvelopeSchema.safeParse(body);
    throw envelope.success
      ? new ApiRequestError(
          envelope.data.error.message,
          response.status,
          envelope.data.error.code,
          envelope.data.error.details,
        )
      : new ApiRequestError(t("error.unexpected"), response.status);
  }

  return body;
}

/**
 * `GET /healthz`.
 *
 * A 503 from the api is a *successful* health check reporting unhealthy — the
 * splash has to tell "Postgres is down" apart from "the api is not there at
 * all", so this reads the body on both paths rather than throwing on !ok.
 */
export async function fetchHealth(): Promise<HealthResponse> {
  const base = requireApiUrl();

  let response: Response;
  try {
    response = await fetch(`${base}/healthz`, { headers: { accept: "application/json" } });
  } catch {
    throw new ApiRequestError(t("error.network"), 0, "unavailable");
  }

  const body: unknown = await response.json().catch(() => undefined);
  const parsed = healthResponseSchema.safeParse(body);
  if (!parsed.success) {
    throw new ApiRequestError(t("error.unexpected"), response.status);
  }
  return parsed.data;
}

/**
 * Structural check on the join envelope that does **not** rebuild it.
 *
 * ── LOCKED (architecture §16 A8 + A11) ───────────────────────────────────────
 *
 * `reservation` must reach `consumeSeatReservation()` as the object
 * `JSON.parse` produced — same reference, same keys, same order. That rules out
 * `joinRoomResponseSchema.parse(body)` here even though the schema is a
 * `looseObject`: Zod *validates* loosely but *returns a reconstructed object*,
 * and a reconstruction is exactly the class of "tidying" A8 exists to forbid.
 * Colyseus 0.17 already sends more fields than the five we name, and the day it
 * adds another, a client that re-typed the object is a runtime join failure
 * with no compile-time warning.
 *
 * So: assert enough to fail fast and clearly, then hand the original through.
 * `redirected` is checked as a required boolean — A11: clients must not treat
 * it as optional.
 */
function assertJoinEnvelope(body: unknown): JoinRoomResponse {
  if (typeof body !== "object" || body === null) {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  const envelope = body as { reservation?: unknown; redirected?: unknown };

  if (typeof envelope.redirected !== "boolean") {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  const reservation = envelope.reservation;
  if (typeof reservation !== "object" || reservation === null) {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  const seat = reservation as { sessionId?: unknown; roomId?: unknown };
  if (typeof seat.sessionId !== "string" || typeof seat.roomId !== "string") {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }

  // The SAME object, not a copy. This line is the point of the function.
  return {
    reservation: reservation as SeatReservation,
    redirected: envelope.redirected,
  };
}

/**
 * `GET /api/v1/lobby` — the read model (S3-3).
 *
 * **Parsed, unlike the reservation.** The opposite call is made here on purpose:
 * a reservation must survive verbatim because Colyseus owns its shape and adds
 * to it, while the lobby body is *our* contract and a field that arrives wrong
 * should fail here rather than three components deep, where the symptom is a
 * blank tile and the cause is a rename nobody redeployed for.
 */
export async function fetchLobby(): Promise<LobbyResponse> {
  const body = await request(`${API_V1}/lobby`);
  const parsed = lobbyResponseSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/**
 * `POST /api/v1/lobby/watch` — a seat in the live lobby room (S3-4).
 *
 * The reservation is handed back **untouched** (§16 A8): same object, same
 * keys, straight into `consumeSeatReservation()`. Same reasoning, and the same
 * deliberate absence of a Zod parse, as `postRoomJoin`.
 */
export async function postLobbyWatch(): Promise<{ reservation: SeatReservation }> {
  const body = await request(`${API_V1}/lobby/watch`, { method: "POST" });
  if (typeof body !== "object" || body === null) {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  const reservation = (body as { reservation?: unknown }).reservation;
  const seat = reservation as { sessionId?: unknown; roomId?: unknown } | null;
  if (!seat || typeof seat.sessionId !== "string" || typeof seat.roomId !== "string") {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  // The SAME object, not a copy.
  return { reservation: reservation as SeatReservation };
}

/**
 * `POST /api/v1/rooms/join` — the real, money-carrying join (S4-5).
 *
 * The client names a **tile** (`roomConfigId`) and never a room: matchmaking is
 * the server's (§3.3). The envelope is the `{ reservation, redirected }` shape
 * every seat-issuing route has returned since S1 (§16 A11) — so the caller
 * unwraps it **once** and hands `reservation` straight to `consumeSeatReservation()`
 * without touching it (§16 A8/A11). `assertJoinEnvelope` is the same
 * structural check for the same reason: Zod would *rebuild* the object, and a
 * rebuilt reservation is a socket pointed at the wrong process.
 *
 * A 403 with `details.reason === "insufficient_funds"` is the locked-tile case
 * and carries the caller's balance; the `ApiRequestError` keeps the status and
 * the envelope's code, and the screen reads Ivy's copy off its own balance
 * (§9.1: one truth).
 */
export async function postRoomJoin(
  roomConfigId: string,
  /**
   * The room the **tile the player tapped** was displaying (lobby §12.4,
   * LOCKED: "a tile join always sends the room id the tile rendered").
   *
   * It is what makes `redirected` mean what architecture §3.3 said it means —
   * the server compares and answers truthfully, instead of the flag only being
   * reachable in a millisecond race inside the call (T-208 N4).
   *
   * **`Play again` and `Join again` send nothing here**, deliberately: they are
   * not tile taps, they set no rendered expectation, and both are LOCKED never
   * to raise the redirect toast (room spec §8.4, §9 T4).
   */
  roomId?: string | null,
): Promise<JoinRoomResponse> {
  const body = await request(`${API_V1}/rooms/join`, {
    method: "POST",
    body: JSON.stringify(roomId ? { roomConfigId, roomId } : { roomConfigId }),
  });
  return assertJoinEnvelope(body);
}

/**
 * `POST /api/v1/rooms/:id/leave` — pre-game leave, refunded in full.
 *
 * **Parsed, unlike the reservation**: this body is ours, and `refundedCoins`
 * arriving wrong should fail here rather than as a wrong number on a screen that
 * has just told a player their money came back. `roomId` may be either
 * identifier the client holds — the one from the reservation, or the one in
 * room state.
 */
export async function postRoomLeave(roomId: string): Promise<LeaveRoomResponse> {
  const body = await request(`${API_V1}/rooms/${encodeURIComponent(roomId)}/leave`, {
    method: "POST",
  });
  const parsed = leaveRoomResponseSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/**
 * `POST /api/v1/rooms/:id/rejoin` — back into the seat you already hold
 * (architecture §16f **A42**).
 *
 * This is what a **cold reload or a deep link** on `/room/:id` calls. It is not
 * `/rooms/join`: join resolves the *config's* filling room, which is a different
 * room and a second charge. This one names the room and moves no money at all —
 * the paid `room_participants` row is the authorisation.
 *
 * `roomId` may be either identifier: the URL segment (Colyseus's) is what a cold
 * reload has, and room state carries ours.
 *
 * The reservation is handed back **untouched** (§16 A8/A11) for the usual reason
 * and one extra: it carries `reconnectionToken`, and a rebuilt object without it
 * is a socket that lands beside the player's seat instead of in it.
 */
export async function postRoomRejoin(roomId: string): Promise<{
  reservation: SeatReservation;
  roomId: string;
  roomConfigId: string;
  seatIndex: number;
}> {
  const body = await request(`${API_V1}/rooms/${encodeURIComponent(roomId)}/rejoin`, {
    method: "POST",
  });
  if (typeof body !== "object" || body === null) {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  const envelope = body as {
    reservation?: unknown;
    roomId?: unknown;
    roomConfigId?: unknown;
    seatIndex?: unknown;
  };
  const reservation = envelope.reservation as { sessionId?: unknown; roomId?: unknown } | null;
  if (
    typeof reservation !== "object" ||
    reservation === null ||
    typeof reservation.sessionId !== "string" ||
    typeof reservation.roomId !== "string" ||
    typeof envelope.roomId !== "string" ||
    typeof envelope.roomConfigId !== "string" ||
    typeof envelope.seatIndex !== "number"
  ) {
    throw new ApiRequestError(t("error.unexpected"), 200);
  }
  return {
    // The SAME object, not a copy.
    reservation: reservation as SeatReservation,
    roomId: envelope.roomId,
    roomConfigId: envelope.roomConfigId,
    seatIndex: envelope.seatIndex,
  };
}

/**
 * The facts a refused rejoin carries (**A42's failure half**).
 *
 * `null` for anything that is not a rejoin refusal — a network failure, a 401, a
 * body missing a field. `null` means *we could not find out*, which is T6, and
 * that is the whole point of the distinction: T6 stops meaning "we did not ask".
 *
 * **All-or-nothing on purpose.** A partial read would render a terminal from
 * half a refusal, and the missing half is always the money. Read here rather
 * than in the screen so the narrowing exists once and `details` stays untyped in
 * `ApiRequestError`, as every other refusal reads it.
 */
export function rejoinFactsOf(error: unknown): RejoinRefusalFacts | null {
  const reason = reasonOf(error);
  if (reason === null) return null;
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const body = details as Record<string, unknown>;

  /**
   * **The room half arrives nested, as `RoomSummary`** (A45). The nesting is the
   * reason this read is worth its own narrowing: the body carries two fields
   * called `prizeCoins` — the room's promise inside `room`, and this player's
   * credit beside it — and reading them off one flat object is exactly the
   * confusion A45 split the shape to prevent.
   */
  const summary = body.room;
  if (typeof summary !== "object" || summary === null) return null;
  const roomFields = summary as Record<string, unknown>;

  const str = (source: Record<string, unknown>, field: string): string | null =>
    typeof source[field] === "string" ? (source[field] as string) : null;
  const num = (source: Record<string, unknown>, field: string): number | null =>
    typeof source[field] === "number" && Number.isFinite(source[field])
      ? (source[field] as number)
      : null;
  /**
   * **A field that is legally `null` still has to be *present*** (T-217, Quinn
   * N3). `seatOutcome` and `room.cancelReason` now travel as `null` rather than
   * `""` when the server has no value, so the all-or-nothing read below cannot
   * use `null` as its "missing" marker for those two — it would reject every
   * completed and cancelled room and render T6 in place of T8 / T1 / T3.
   * `undefined` means the key was absent or the wrong type, which is still a
   * partial refusal and still T6.
   */
  const nullableStr = (
    source: Record<string, unknown>,
    field: string,
  ): string | null | undefined => {
    const value = source[field];
    if (value === null) return null;
    return typeof value === "string" ? value : undefined;
  };

  const seatOutcome = nullableStr(body, "seatOutcome");
  const roomId = str(roomFields, "roomId");
  const roomConfigId = str(roomFields, "roomConfigId");
  const gameTypeName = str(roomFields, "gameTypeName");
  const gameModeName = str(roomFields, "gameModeName");
  const entryFeeCoins = num(roomFields, "entryFeeCoins");
  const prizeCoins = num(roomFields, "prizeCoins");
  const phase = str(roomFields, "phase");
  const cancelReason = nullableStr(roomFields, "cancelReason");

  if (
    seatOutcome === undefined ||
    cancelReason === undefined ||
    roomId === null ||
    roomConfigId === null ||
    gameTypeName === null ||
    gameModeName === null ||
    entryFeeCoins === null ||
    prizeCoins === null ||
    phase === null
  ) {
    return null;
  }

  return {
    reason,
    room: {
      roomId,
      roomConfigId,
      gameTypeName,
      gameModeName,
      entryFeeCoins,
      prizeCoins,
      phase,
      cancelReason,
    },
    seatOutcome,
    /**
     * **`me.prizeCoins` is the one field whose absence is not all-or-nothing.**
     * `null` is a *legal, meaningful* value here — "the server cannot say" — and
     * on a paid room the selector turns it into T6 (§9.9). Treating a missing
     * field as `null` is therefore not a partial read: it is the same statement
     * the server makes explicitly, and it lands on the same panel.
     */
    prizeCoins: num(body, "prizeCoins"),
  };
}

/**
 * `GET /api/v1/rewards/daily` — the daily reward row's only read (DR-5, §21.9).
 *
 * **Parsed**, like the lobby body and unlike a reservation: this is our own
 * contract, and a `state` the client has never heard of should fail here rather
 * than three components deep. Failing here is also *correct behaviour* rather
 * than a dead end — §21.9's recovery rule for this surface is **render
 * nothing**, and Ivy's §21.3 addendum extends that to any unknown state, so a
 * rejected body and an unhandled state land on the same silence.
 */
export async function fetchDailyReward(): Promise<DailyReward> {
  const body = await request(`${API_V1}/rewards/daily`);
  const parsed = dailyRewardSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/**
 * `POST /api/v1/rewards/daily/claim` — **sent with no body and therefore no
 * `content-type` header, and that is a documented contract, not an accident.**
 *
 * > *"The claim POST is sent with no request body and no `content-type` header
 * > … Changing `api`'s content-type parsing is out of scope and is a
 * > stop-and-ask."* — §16o addendum 3, routed question 1 (Dex, 2026-08-06)
 *
 * The route reads no body, no query and no header: the claimant is the session
 * cookie and the day is the server's UTC date (A103 criterion 8 — the client
 * cannot move the day). Fastify refuses an empty body under
 * `content-type: application/json` with a `400` **before any handler runs**, so
 * a wrapper that stamped that header unconditionally would 400 every claim. This
 * one does not: `request()` adds `content-type` only when there is an
 * `init.body`, so passing none is what sends none. **A 400 from this route is a
 * client defect, never a server one** — if a future edit makes the header
 * unconditional, the sanctioned fallback is to send the literal body `{}`, still
 * a client-side change.
 *
 * Every refusal is a **200 with a state**, never a 4xx (§21.4): `credited: false`
 * is not an error and is not announced, because a refusal must be
 * indistinguishable in tone from a success that already happened. Only a
 * transport failure or a genuine non-2xx throws from here.
 */
export async function postDailyRewardClaim(): Promise<DailyRewardClaim> {
  const body = await request(`${API_V1}/rewards/daily/claim`, { method: "POST" });
  const parsed = dailyRewardClaimSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/* ==========================================================================
   The shop — S8 E7 `SHOP-STOREFRONT-WEB` (T-404). Three existing routes
   (T-401 `SHOP-API-READ`, T-402 `SHOP-PURCHASE-SEAM`); this ticket adds no
   route, only the client wrappers that call them.
   ========================================================================== */

/**
 * `GET /api/v1/shop/catalog`.
 *
 * **Never called while the flag is off.** Ivy §4.2 is LOCKED that the closed
 * screen fetches no catalog at all — the caller branches on
 * `useShopFlag()`'s `enabled` first. A `409 shop_unavailable` reaching this
 * function anyway is the flag-flip race binding input 1 names; it surfaces
 * here as an ordinary `ApiRequestError` and the caller renders the closed
 * panel for it, never the catalog-error panel (§4.2 addendum b, LOCKED).
 *
 * Parsed, like every other body that is our own contract: a shape drift
 * should fail here, not three components into a rendered grid.
 */
export async function fetchShopCatalog(): Promise<ShopCatalog> {
  const body = await request(`${API_V1}/shop/catalog`);
  const parsed = shopCatalogSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/** `GET /api/v1/shop/inventory` — the caller's own owned-item ids. */
export async function fetchShopInventory(): Promise<ShopInventory> {
  const body = await request(`${API_V1}/shop/inventory`);
  const parsed = shopInventorySchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/**
 * `POST /api/v1/shop/purchase` — the real seam (T-402). Every well-formed
 * request answers `409 shop_unavailable` in this era (the seam's handler is
 * typed `Promise<never>`); this function still parses a 2xx body against the
 * frozen-but-unreachable `shopPurchaseSuccessSchema` rather than assuming the
 * shape, so the day the pricing wave makes a 2xx reachable this call site
 * needs no change.
 *
 * **`expectPriceCoins` is always sent** (binding input 2) — `null` in this
 * era, since nothing is priced. Omitting it is a 400 with no designed
 * register; the caller never has a reason to.
 */
export async function postShopPurchase(payload: ShopPurchaseRequest): Promise<ShopPurchaseSuccess> {
  const body = await request(`${API_V1}/shop/purchase`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const parsed = shopPurchaseSuccessSchema.safeParse(body);
  if (!parsed.success) throw new ApiRequestError(t("error.unexpected"), 200);
  return parsed.data;
}

/** Exported for the unit test; not part of the module's public surface. */
export const __testing = { assertJoinEnvelope };
